import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from "@azure/msal-angular";
import {
  AccountInfo, AuthenticationResult, AuthError, BrowserAuthError, EventError, EventMessage,
  EventType, InteractionStatus, InteractionType, PopupRequest, RedirectRequest, SsoSilentRequest, SilentRequest, InteractionRequiredAuthError
} from "@azure/msal-browser";
import { IdTokenClaims, PromptValue } from '@azure/msal-common';
import { filter, Observable, Observer, Subject, takeUntil, of, switchMap, catchError } from "rxjs";
import { AuthConfig } from "../../model/auth-config";
import { AuthUser } from "../../model/auth-user";

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string
};

type AppIdTokenClaims = IdTokenClaims & {
  family_name?: string,
  given_name?: string,
  extension_MobileNumber?: string
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  private authUserSubject: Subject<AuthUser>;
  private readonly destroying$ = new Subject<void>();


  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private broadcastService: MsalBroadcastService,
    private authConfig: AuthConfig,
    private httpClient: HttpClient,
    @Inject('BASE_URL') private baseURL: string
  ) {


    this.authUserSubject = new Subject<AuthUser>();
    this.destroying$ = new Subject<void>();

    this.msalService.instance.enableAccountStorageEvents();

    this.broadcastService.msalSubject$.pipe(
      filter(
        (msg: EventMessage) => msg.eventType ===
          EventType.ACCOUNT_ADDED
          || msg.eventType === EventType.ACCOUNT_REMOVED))
      .subscribe((result: EventMessage) => {
        this.refreshAuthUser();
      });

    this.broadcastService.msalSubject$.pipe(filter(
      (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
        || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        || msg.eventType === EventType.SSO_SILENT_SUCCESS),
      takeUntil(this.destroying$))
      .subscribe((result: EventMessage) => {

        this.refreshAuthUser();
        let payload = result.payload as AuthenticationResult;
        let idToken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (this.hasEditProfilePolicyId(idToken)) {
          this.onEditProfileSuccess(idToken);

        }

        /**
                 * Below we are checking if the user is returning from the reset password flow.
                 * If so, we will ask the user to reauthenticate with their new password.
                 */
        if (this.hasPasswordResetPolicyId(idToken)) {
          this.onPasswordReset();
        }
      });


    this.broadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this.destroying$)
      )
      .subscribe((result: EventMessage) => {
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (this.hasForgotPasswordErrorCode(result.error)) {
          this.handleForgotPassword();
        };
      });


    this.broadcastService.inProgress$
      .pipe(filter(status => status === InteractionStatus.None),
        takeUntil(this.destroying$))
      .subscribe(_ => this.refreshAuthUser());
  }

  handleForgotPassword() {
    let resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
      authority: this.authConfig.passwordReset.authority,
      scopes: [],
    };

    this.login(resetPasswordFlowRequest);
  }


  hasForgotPasswordErrorCode(error: EventError) {
    const ForgotPasswordCode = 'AADB2C90118';
    return error && error.message.indexOf(ForgotPasswordCode) > -1
  }

  hasPasswordResetPolicyId(idToken: IdTokenClaimsWithPolicyId) {
    return idToken.acr === this.authConfig.passwordReset.policyName || idToken.tfp === this.authConfig.passwordReset.policyName;
  }

  onPasswordReset() {
    let signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
      authority: this.authConfig.signUpSignIn.authority,
      scopes: this.authConfig.scopes,
      prompt: PromptValue.LOGIN // force user to reauthenticate with their new password
    };

    this.login(signUpSignInFlowRequest);
  }


  onEditProfileSuccess(idToken: IdTokenClaimsWithPolicyId) {
    // retrieve the account from initial sing-in to the app
    const originalSignInAccount = this.msalService.instance.getAllAccounts()
      .find((account: AccountInfo) =>
        account.idTokenClaims?.sub === idToken.sub
        && ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr === this.authConfig.signUpSignIn.policyName
          || (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp === this.authConfig.signUpSignIn.policyName)
      );

    let signUpSignInFlowRequest: SsoSilentRequest = {
      authority: this.authConfig.signUpSignIn.authority,
      account: originalSignInAccount,
    };

    // silently login again with the signUpSignIn policy
    this.msalService.ssoSilent(signUpSignInFlowRequest);
  }

  onSignInSuccess(authenticationResult: AuthenticationResult) {
    this.msalService.instance.setActiveAccount(authenticationResult.account);
    this.refreshAuthUser();
  }

  hasEditProfilePolicyId(idToken: IdTokenClaimsWithPolicyId) {
    return idToken.acr === this.authConfig.editProfile.policyName || idToken.tfp === this.authConfig.editProfile.policyName;
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }

  get authUserObservable$(): Observable<AuthUser> {
    return this.authUserSubject.asObservable();
  }

  get currentAuthUser(): AuthUser {
    return this.buildUser(this.getActiveAccount());
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest): Observable<void> {
    console.log('User Flow Request:', userFlowRequest);
    if (this.msalGuardConfig.authRequest) {
      let request: RedirectRequest = {
        ...this.msalGuardConfig.authRequest, ...userFlowRequest
      };

      request.redirectStartPage = this.authConfig.redirectStartPage;
      request.scopes = this.authConfig.scopes;

      return this.msalService
        .loginRedirect(request);
    } else {
      console.log('authRequest ' + this.msalGuardConfig.authRequest);

      return this.msalService.loginRedirect(userFlowRequest);
    }
  }

  performSignIn() {
    var t = this.httpClient.get<any>(this.baseURL + 'api/Signin/PerformSignin')
    return t;
  }

  register() {
    let registrationFlowRequest: RedirectRequest = {
      authority: this.authConfig.registration.authority,
      scopes: []
    }
    this.login(registrationFlowRequest)
  }

  logout(): Observable<void> {
    return this.msalService.logoutRedirect();
  }

  editProfile() {
    let editProfileFlowRequest: RedirectRequest = {
      authority: this.authConfig.editProfile.authority,
      scopes: [],
      //redirectStartPage: '/dashboard'
    }
    this.login(editProfileFlowRequest);
  }

  public accessToken(): Observable<string> {    

    const activeAccount = this.getActiveAccount();

    if (!activeAccount) {
      return of(null);
    }

    const tokenRequest: SilentRequest = {
      scopes: this.authConfig.scopes,
      account: activeAccount,
    };

    return this.msalService.acquireTokenSilent(tokenRequest).pipe(
      switchMap((authResult) => of(authResult?.accessToken || null)),
      catchError((error) => {
        // Check if the error indicates that an interactive authentication is needed.
        if (error instanceof InteractionRequiredAuthError) {
          this.msalService.acquireTokenRedirect(tokenRequest);
          return of(null);
        }
        // Log and return null for any error.
        console.error('Token acquisition error:', error);
        return of(null);
      })
    );
  }

  private buildUser(accountInfo: AccountInfo): AuthUser {
    if (accountInfo === null) return null;

    const claims = accountInfo.idTokenClaims as AppIdTokenClaims;
    return {
      firstName: claims.given_name,
      lastName: claims.family_name,
      name: accountInfo.name,
      id: claims.sub,
      email: accountInfo.username,
      mobileNumber: claims.extension_MobileNumber
    };
  }

  private refreshAuthUser() {
    /**
  * If no active account set but there are accounts signed in, sets first account to active account
  * To use active account set here, subscribe to inProgress$ first in your component
  * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
  */
    const activeAccount = this.getActiveAccount();
    if (activeAccount) {
      this.msalService.instance.setActiveAccount(activeAccount);
      this.authUserSubject.next(this.buildUser(activeAccount));
    } else {
      this.authUserSubject.next(undefined);
    }
  }

  private getActiveAccount(): AccountInfo | null {
    let activeAccount = this.msalService.instance.getActiveAccount();
    let accts = this.msalService.instance.getAllAccounts();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.msalService.instance.getAllAccounts();
      activeAccount = accounts[0];
      return activeAccount;
    }
    return activeAccount;
  }
}
